import React, { useEffect, useState } from "react";
import { PageHeader } from "@/pages/About";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { contentApi } from "@/lib/api";
import { Download, FileText, Loader2 } from "lucide-react";

const CodeOfConduct = () => {
  const [coc, setCoc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.codeOfConduct().then(({ data }) => setCoc(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader eyebrow="Standard" title="WECH-RB Code of Conduct"
        subtitle="The standard against which every applicant is assessed. Download the full Code of Conduct or explore each chapter below." />
      <section className="wechr-section">
        <div className="wechr-container">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 rounded-xl border border-[var(--wechr-border)] bg-[var(--wechr-surface)] p-6 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-wgreen-600" />
              <div>
                <p className="font-heading font-semibold text-navy-900">WECH-RB Code of Conduct (PDF)</p>
                <p className="text-sm text-[var(--wechr-muted-text)]">Complete downloadable standard document.</p>
              </div>
            </div>
            <a href={contentApi.cocPdfUrl()} target="_blank" rel="noreferrer" data-testid="coc-download-button">
              <Button className="btn-green"><Download className="mr-2 h-4 w-4" /> Download PDF</Button>
            </a>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-wgreen-600" /></div>
          ) : coc ? (
            <>
              {coc.meta && (
                <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 rounded-lg border border-[var(--wechr-border)] bg-white px-4 py-3 text-xs text-[var(--wechr-muted-text)]" data-testid="coc-meta">
                  <span><span className="font-semibold text-navy-900">Document:</span> {coc.meta.document_number}</span>
                  <span><span className="font-semibold text-navy-900">Version:</span> {coc.meta.version}</span>
                  <span><span className="font-semibold text-navy-900">Status:</span> {coc.meta.status}</span>
                  <span><span className="font-semibold text-navy-900">Effective:</span> {coc.meta.effective_date}</span>
                </div>
              )}
              <Card className="mb-6"><CardContent className="p-6"><p className="leading-relaxed text-navy-800">{coc.preamble}</p></CardContent></Card>
              <Accordion type="multiple" className="space-y-3" defaultValue={["ch-1"]}>
                {coc.chapters.map((ch) => (
                  <AccordionItem key={ch.number} value={`ch-${ch.number}`} className="rounded-xl border border-[var(--wechr-border)] bg-white px-4">
                    <AccordionTrigger className="text-left font-heading text-base font-semibold text-navy-900 hover:no-underline">
                      Chapter {ch.number}. {ch.title}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pb-2">
                        {ch.topics.map((t) => (
                          <div key={t.name}>
                            <p className="font-medium text-navy-900">{t.name}</p>
                            <p className="mt-0.5 text-sm leading-relaxed text-[var(--wechr-muted-text)]">{t.text}</p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              {coc.closing && (
                <Card className="mt-6"><CardContent className="p-6">
                  <h3 className="font-heading text-base font-semibold text-navy-900">Certification Review &amp; Applicant Acknowledgment</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--wechr-muted-text)]">{coc.closing}</p>
                </CardContent></Card>
              )}
            </>
          ) : (
            <p className="text-[var(--wechr-muted-text)]">Unable to load the Code of Conduct.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default CodeOfConduct;
